/* eslint-disable @typescript-eslint/indent */
import Grid from '@material-ui/core/Grid';
import capitalize from '@material-ui/core/utils/capitalize';
import PeButton from '@pe-libs/react-components/build/components/PeButton';
import PePagination from '@pe-libs/react-components/build/components/PePagination';
import PeTypography from '@pe-libs/react-components/build/components/PeTypography';
import useImage from '@pe-libs/react-components/build/lib/hooks/useImage';
import useScroll from '@pe-libs/react-components/build/lib/hooks/useScroll';
import useIntlMessage from '@pe-libs/react-components/build/lib/intl/hooks/useIntlMessage';
import useIntlNumber from '@pe-libs/react-components/build/lib/intl/hooks/useIntlNumber';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import imageDesktop from '#assets/images/expertDirectory/resultsDesktop.jpg';
import imageMobile from '#assets/images/expertDirectory/resultsMobile.jpg';
import imageTablet from '#assets/images/expertDirectory/resultsTablet.jpg';
import Footer from '#components/Footer';
import { getKeyword, getLocation, getQueryParameters } from '#helpers/query';
import { getSearchUrl } from '#helpers/searchUrl';
import { getUserLocale } from '#modules/app/selectors';
import { leadFormOpened, loadingChanged } from '#modules/expertDirectory/actions';
import CreateLead from '#modules/expertDirectory/components/CreateLead';
import { generateQueryFromUrl } from '#modules/expertDirectory/helpers';
import {
  getSearchResult,
  getSearchResultContext,
  getSearchResultMeta,
} from '#modules/expertDirectory/selectors/searchDirectory';
import BottomSearchSection from '../BottomSearchSection';
import NoResultsPage from '../NoResultsPage';
import SearchSection from '../SearchSection';
import Banner from './Banner';
import { generatePaginatedUrl } from './helper';
import messages from './messages';
import SingleItemList from './SingleItemList';
import useStyles from './styles';

const Results: React.FC = () => {
  const { query, push, asPath } = useRouter();
  const styles = useStyles();
  const formatMessage = useIntlMessage();
  const formatNumber = useIntlNumber();
  const { scrollTo } = useScroll();
  const locale = useSelector(getUserLocale)?.toLowerCase() ?? 'de-de';
  const defaultLocation = locale === 'de-de' ? 'deutschland' : 'germany';
  const keyword = getKeyword(query);
  const searchContext = useSelector(getSearchResultContext);
  const location = searchContext?.location?.name || getLocation(query) || defaultLocation;
  const page = Number(getQueryParameters(query, 'page'));

  const backgroundImg = useImage(imageMobile, imageTablet, imageDesktop);

  const {
    data,
    meta: { count },
  } = useSelector(getSearchResult);
  const meta = useSelector(getSearchResultMeta);

  const dispatch = useDispatch();

  const handleModalOpen = () => {
    dispatch(leadFormOpened());
  };

  useEffect(() => {
    if (query.openLeadForm) {
      dispatch(leadFormOpened());
    }
  }, [query.openLeadForm]);

  const handleSearchButton = () => {
    push(`/${locale}/${getSearchUrl(locale)}/?keyword=${keyword}${location ? `&location=${location}` : ''}`);
  };

  const handleChangePage = async (event: React.ChangeEvent<unknown>, nextPage: number) => {
    dispatch(loadingChanged(true));

    await push(generatePaginatedUrl({ nextPage, asPath, currentPage: Number(page) }));

    dispatch(loadingChanged(false));
    scrollTo('results', -64);
  };

  const getCounter = (index: number) => {
    if (Number(page) > 1) {
      return Number(page) * 10 + index + 1 - 10;
    }

    return index + 1;
  };

  return data && data.length > 0 ? (
    <>
      <SearchSection className={styles.resultsPage} bgImage={backgroundImg} hasSearchBar={false}>
        <Grid container={true} justify="center" className={styles.container}>
          <Grid xs={12} sm={10} md={8} item={true} className={styles.center}>
            <PeTypography variant="h1" className={styles.hyphensAuto}>
              {formatMessage(messages.title, {
                count: formatNumber(count ?? 0, { minimumFractionDigits: 0 }),
                keyword: capitalize(generateQueryFromUrl(keyword)),
                location: capitalize(generateQueryFromUrl(location)),
              })}
            </PeTypography>
            <p className={styles.pageDescription}>
              {formatMessage(messages.resultsPageDescription, {
                count: formatNumber(count ?? 0, { minimumFractionDigits: 0 }),
                keyword: capitalize(generateQueryFromUrl(keyword)),
                location: capitalize(generateQueryFromUrl(location)),
              })}
            </p>
            <PeButton
              title={formatMessage(messages.hoverSerpModalCta)}
              variant="contained"
              color="warning"
              onClick={handleModalOpen}
              aria-label={`${formatMessage(messages.redirectionCta)} button`}
            >
              {formatMessage(messages.redirectionCta)}
            </PeButton>
          </Grid>
          <Grid container={true} spacing={6}>
            <Grid xs={12} md={4} item={true} className={styles.center}>
              <CreateLead />
            </Grid>
          </Grid>
        </Grid>
      </SearchSection>
      <div className={styles.resultSection} id="results">
        <Grid container={true} justify="center" className={styles.center}>
          <Grid xs={12} md={10} item={true} className={styles.resultContainer}>
            <div className={styles.buttonContainer}>
              <PeButton
                title={formatMessage(messages.hoverAdjustSearch)}
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleSearchButton}
                aria-label={`${formatMessage(messages.adjustSearch)} button`}
              >
                {formatMessage(messages.adjustSearch)}
              </PeButton>
            </div>
          </Grid>
        </Grid>
        <Grid container={true} justify="center">
          <Grid xs={12} md={10} item={true} className={styles.resultContainer}>
            <ol className={styles.resultList}>
              {data.map((result, index) => (
                <div key={result.profile.createdAt}>
                  <SingleItemList data={result} counter={getCounter(index)} />
                  {data.length >= 5
                    ? index === 4 && <Banner handleChangePage={handleChangePage} />
                    : index === data.length - 1 && <Banner handleChangePage={handleChangePage} />}
                </div>
              ))}
            </ol>
            <div className={styles.pagination}>
              <PePagination
                count={meta.totalPages}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onChange={handleChangePage}
                page={meta.page}
                disabled={count === 1}
              />
            </div>
          </Grid>
        </Grid>
      </div>
      <BottomSearchSection />
      <Footer />
    </>
  ) : (
    <NoResultsPage />
  );
};

export default Results;
